<template>
  <div class="mt-6">
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">
          FAQ Generation
        </h3>
        <button
          v-if="canGenerate"
          @click="startGeneration"
          :disabled="loading"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
          Generate FAQs
        </button>
      </div>

      <!-- Progress -->
      <div v-if="generating" class="mb-4">
        <div class="flex items-center gap-2 text-sm text-primary-500">
          <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          <span>{{ progressMessage }}</span>
        </div>
      </div>

      <!-- Finalized State -->
      <div v-if="faq?.status === 'schema_generated' || faq?.status === 'completed'" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-4">
        <div class="flex items-center gap-2">
          <svg class="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>
          <span class="text-green-700 dark:text-green-300 font-medium">FAQs finalized with JSON-LD schema!</span>
        </div>
        <p class="text-sm text-green-600 dark:text-green-400 mt-1">
          {{ faq.questions?.length }} FAQs · {{ faq.total_faq_words }} words
        </p>
      </div>

      <!-- FAQ Review (status: ready) -->
      <div v-if="faq?.status === 'ready'">
        <p class="text-sm text-gray-500 mb-3">
          Review and approve the generated FAQs. You can remove individual questions.
        </p>

        <div v-for="(q, i) in editableQuestions" :key="i" class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h4 class="font-medium text-gray-900 dark:text-gray-100">
                {{ q.question }}
              </h4>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {{ q.answer }}
              </p>
              <div class="flex items-center gap-2 mt-2">
                <span class="text-xs px-2 py-0.5 rounded-full" :class="sourceClass(q.source)">{{ q.source }}</span>
                <span class="text-xs text-gray-400">{{ q.word_count }} words</span>
                <span v-for="kw in q.keywords_used" :key="kw" class="text-xs px-2 py-0.5 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-full">{{ kw }}</span>
              </div>
            </div>
            <button @click="removeQuestion(i)" class="ml-2 text-gray-400 hover:text-red-500">
              <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
            </button>
          </div>
        </div>

        <button
          @click="approveAllFaqs"
          :disabled="loading || !editableQuestions.length"
          class="px-4 py-2 bg-[#12e40b] text-white rounded-lg text-sm font-medium hover:bg-[#0fd609] disabled:opacity-50">
          Approve {{ editableQuestions.length }} FAQs
        </button>
      </div>

      <!-- Impact Assessment / Corrections -->
      <div v-if="faq?.status === 'impact_assessed' || faq?.status === 'corrections_proposed'">
        <!-- Keyword Impact Table -->
        <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Keyword Impact Assessment
        </h4>
        <div class="overflow-x-auto mb-4">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 bg-gray-50 dark:bg-gray-900/50">
                <th class="text-left py-2 px-2 text-gray-500 font-medium">
                  Keyword
                </th>
                <th class="text-center py-2 px-2 text-gray-500 font-medium">
                  Body
                </th>
                <th class="text-center py-2 px-2 text-gray-500 font-medium">
                  FAQ
                </th>
                <th class="text-center py-2 px-2 text-gray-500 font-medium">
                  Projected
                </th>
                <th class="text-center py-2 px-2 text-gray-500 font-medium">
                  Target
                </th>
                <th class="text-center py-2 px-2 text-gray-500 font-medium">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, kw) in faq.keyword_impact" :key="kw" class="border-b border-gray-100">
                <td class="py-2 px-2 font-medium">
                  {{ kw }}
                </td>
                <td class="py-2 px-2 text-center">
                  {{ data.body_count }}
                </td>
                <td class="py-2 px-2 text-center">
                  {{ data.faq_count }}
                </td>
                <td class="py-2 px-2 text-center font-mono">
                  {{ data.projected_total }}
                </td>
                <td class="py-2 px-2 text-center">
                  {{ data.target }}
                </td>
                <td class="py-2 px-2 text-center">
                  <span class="px-2 py-0.5 rounded-full text-xs" :class="impactStatusClass(data.status)">{{ data.status }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Body Corrections -->
        <div v-if="faq.body_corrections?.length && faq.status === 'corrections_proposed'" class="mb-4">
          <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Body Corrections ({{ faq.body_corrections.length }})
          </h4>
          <div v-for="(fix, i) in faq.body_corrections" :key="i" class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-2">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-xs font-medium px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg">{{ fix.keyword }}</span>
              <span class="text-xs text-gray-500">Section {{ fix.section_index }} · {{ fix.count_change > 0 ? '+' : '' }}{{ fix.count_change }}</span>
            </div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                <span class="text-xs text-red-500 font-medium">Remove:</span>
                <p class="text-red-800 dark:text-red-300 mt-0.5">
                  {{ fix.original_text }}
                </p>
              </div>
              <div class="bg-green-50 dark:bg-green-900/20 p-2 rounded-lg">
                <span class="text-xs text-green-500 font-medium">Replace:</span>
                <p class="text-green-800 dark:text-green-300 mt-0.5">
                  {{ fix.new_text }}
                </p>
              </div>
            </div>
          </div>

          <button
            @click="approveCorrections"
            :disabled="loading"
            class="mt-2 px-4 py-2 bg-[#12e40b] text-white rounded-lg text-sm font-medium hover:bg-[#0fd609] disabled:opacity-50">
            Approve Corrections & Generate Schema
          </button>
        </div>

        <!-- No corrections needed -->
        <div v-if="!faq.body_corrections?.length && faq.status === 'corrections_proposed'">
          <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 mb-3">
            <span class="text-sm text-green-700 dark:text-green-300">No body corrections needed — all keywords on target.</span>
          </div>
          <button
            @click="generateSchema"
            :disabled="loading"
            class="px-4 py-2 bg-[#12e40b] text-white rounded-lg text-sm font-medium hover:bg-[#0fd609] disabled:opacity-50">
            Generate FAQ Schema
          </button>
        </div>
      </div>

      <!-- Approved FAQs list (show in all states after ready) -->
      <div v-if="faq && faq.questions?.length && !['pending', 'generating', 'ready'].includes(faq.status)" class="mt-4">
        <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Approved FAQs ({{ faq.questions.length }})
        </h4>
        <div v-for="(q, i) in faq.questions" :key="i" class="border-l-2 border-primary-200 pl-3 mb-3">
          <p class="font-medium text-sm text-gray-900">
            {{ q.question }}
          </p>
          <p class="text-sm text-gray-600 mt-0.5">
            {{ q.answer }}
          </p>
        </div>
      </div>

      <!-- JSON-LD Schema Preview -->
      <div v-if="faq?.faq_schema?.mainEntity" class="mt-4">
        <details class="text-sm">
          <summary class="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
            View JSON-LD Schema
          </summary>
          <pre class="mt-2 bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">{{ JSON.stringify(faq.faq_schema, null, 2) }}</pre>
        </details>
      </div>

      <!-- No FAQ yet -->
      <div v-if="!faq && !generating" class="text-sm text-gray-500">
        Generate FAQ questions and answers to add structured data to the page.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { generateFaqs, getContentFaq, approveFaqs, approveFaqCorrections, generateFaqSchema } from '~/javascript/api/faq_generation'

const props = defineProps({
  pageId: { type: String, required: true }
})

const faq = ref(null)
const loading = ref(false)
const generating = ref(false)
const progressMessage = ref('')
const editableQuestions = ref([])
let subscription = null
let pollTimer = null

const canGenerate = computed(() => {
  if (generating.value || loading.value) return false
  return !faq.value || faq.value.status === 'pending'
})

function sourceClass(source) {
  const classes = {
    paa: 'bg-primary-100 text-primary-700',
    competitor: 'bg-purple-100 text-purple-700',
    generated: 'bg-gray-100 text-gray-600'
  }
  return classes[source] || 'bg-gray-100 text-gray-600'
}

function impactStatusClass(status) {
  return {
    on_target: 'bg-green-100 text-green-700',
    over: 'bg-red-100 text-red-700',
    under: 'bg-primary-100 text-primary-700'
  }[status] || 'bg-gray-100 text-gray-600'
}

function removeQuestion(idx) {
  editableQuestions.value.splice(idx, 1)
}

async function fetchFaq() {
  try {
    const { data } = await getContentFaq(props.pageId)
    faq.value = data.faq
    if (data.faq?.status === 'ready') {
      editableQuestions.value = [...(data.faq.questions || [])]
    }
    if (data.faq?.status === 'generating') {
      generating.value = true
    }
  } catch (e) {
    console.error('Failed to fetch FAQ:', e)
  }
}

async function startGeneration() {
  loading.value = true
  generating.value = true
  progressMessage.value = 'Generating FAQ questions...'
  try {
    await generateFaqs(props.pageId)
    startPolling()
  } catch (e) {
    generating.value = false
    console.error('Failed to start FAQ generation:', e)
  } finally {
    loading.value = false
  }
}

async function approveAllFaqs() {
  loading.value = true
  generating.value = true
  progressMessage.value = 'Assessing keyword impact...'
  try {
    await approveFaqs(props.pageId, editableQuestions.value)
    startPolling()
  } catch (e) {
    generating.value = false
    console.error('Failed to approve FAQs:', e)
  } finally {
    loading.value = false
  }
}

async function approveCorrections() {
  loading.value = true
  try {
    await approveFaqCorrections(props.pageId)
    await fetchFaq()
  } catch (e) {
    console.error('Failed to approve corrections:', e)
  } finally {
    loading.value = false
  }
}

async function generateSchema() {
  loading.value = true
  try {
    await generateFaqSchema(props.pageId)
    await fetchFaq()
  } catch (e) {
    console.error('Failed to generate schema:', e)
  } finally {
    loading.value = false
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const { data } = await getContentFaq(props.pageId)
      if (data.faq && data.faq.status !== 'generating' && data.faq.status !== 'pending') {
        faq.value = data.faq
        generating.value = false
        if (data.faq.status === 'ready') {
          editableQuestions.value = [...(data.faq.questions || [])]
        }
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function setupCable() {
  subscription = consumer.subscriptions.create(
    { channel: 'ContentOptimizationChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'generating') return
        generating.value = false
        stopPolling()
        fetchFaq()
      }
    }
  )
}

onMounted(() => {
  fetchFaq()
  setupCable()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
